import { Component, OnInit, Input, model, inject, effect } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  ItemView,
  ItemViewProviderDirective
} from '@ntap/core';
import { PrimengConfigService, PrimeNGInputTextComponent } from '@ntap/primeng';
import { first, switchMap, of, tap } from 'rxjs';
import { VrijemeInputOption } from '../range-kompleksni/vrijeme-range-kompleksni.component';

interface Item {
  [key: string]: any;
}

@Component({
  selector: 'app-vrijeme-strukturirani',
  templateUrl: './vrijeme-kompleksni.component.html',
  styleUrls: ['./vrijeme-kompleksni.component.css']
})
export class VrijemeKompleksniComponent
  extends PrimeNGInputTextComponent
  implements OnInit
{
  @Input() od: boolean;
  @Input() range: boolean;

  selectedOption = model<VrijemeInputOption>();

  options: VrijemeInputOption[] = ['Godina', 'Datum'];
  public item: any;
  public datum: any;
  public godina: any;

  readonly locale =
    inject(PrimengConfigService).dateConfig?.calendarLocale || {};

  datumFormControl = new FormControl<Date | null>(null);
  godinaFormControl = new FormControl<number | null>(null);

  private itemViewProvider = inject(ItemViewProviderDirective<ItemView>, {
    optional: true
  });

  constructor() {
    super(inject(DynamicFormService), inject(AttributeStateService));
    effect(() => {
      const option = this.selectedOption();
      this.clearFormControls(option);
    });
  }

  private getInputNames(): { datum: string; godina: string } {
    const name = this.inputParam.attributeMetadata.name;
    const indexOfSuffix = name.lastIndexOf('$');
    const originalSuffix =
      indexOfSuffix < 0 ? '' : name.substring(indexOfSuffix);
    const attrName =
      indexOfSuffix < 0 ? name : name.substring(0, indexOfSuffix);

    return {
      datum: attrName + originalSuffix,
      godina: attrName + '_godina' + originalSuffix
    };
  }

  ngOnInit(): void {
    this.addSubscription(
      this.formService.formGroup$
        .pipe(
          switchMap(() => {
            const { datum, godina } = this.getInputNames();

            this.godinaFormControl = this.formService.registerFormControl(
              godina,
              this.godinaFormControl
            );

            this.datumFormControl = this.formService.registerFormControl(
              datum,
              this.datumFormControl
            );

            return this.itemViewProvider
              ? this.itemViewProvider.item$.pipe(
                  first(),
                  tap((item) => this.initializeFromItem(item))
                )
              : of({});
          })
        )
        .subscribe()
    );
  }

  onSelectOrInputDate(val?: Date | null) {
    // p-calendar ne okida kod rucnog upisa onSelect tako da ovisimo o onBlur,
    // a u tom slucaju uzimamo za vrijednost vec azuriran form model
    const resolvedVal = val === undefined ? this.datumFormControl.value : val;
    if (val === undefined || val !== this.datumFormControl.value) {
      const asUtc = (date: Date) =>
        new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
        );
      if (this.od && !!resolvedVal) {
        const value = asUtc(resolvedVal);
        this.datumFormControl.setValue(value);
      } else if (resolvedVal) {
        const value = asUtc(resolvedVal);
        value.setHours(23, 59, 59, 999);
        this.datumFormControl.setValue(value);
      }
      this.datumFormControl.markAsTouched();
      this.validiraj();
    }
  }

  private initializeFromItem(item: Item): void {
    const { datum, godina } = this.getInputNames();

    const godinaOrNull = item[godina] as number | null;

    const godinaValue =
      godinaOrNull != undefined && godinaOrNull != null ? godinaOrNull : null;
    const datumValue = item[datum] ? new Date(item[datum]) : null;

    if (godinaValue != null) {
      this.setGodinaValue(godinaValue);
    } else if (datumValue != null) {
      this.setDatumValue(datumValue);
    } else {
      if (!this.selectedOption()) {
        this.selectedOption.set('Godina');
      }
      this.godinaFormControl.setValue(null);
      this.datumFormControl.setValue(null);
    }
  }

  private setGodinaValue(value: number | null): void {
    this.selectedOption.set('Godina');
    this.godinaFormControl.setValue(value);
  }

  private setDatumValue(value: Date): void {
    this.selectedOption.set('Datum');
    this.datumFormControl.setValue(value);
  }

  get datumIsInvalid() {
    return (
      this.datumFormControl.touched &&
      this.datumFormControl.dirty &&
      this.datumFormControl.invalid
    );
  }

  get godinaIsInvalid() {
    return (
      this.godinaFormControl.touched &&
      this.godinaFormControl.dirty &&
      this.godinaFormControl.invalid &&
      (this.godinaFormControl.errors?.rangeFromInvalid ||
        this.godinaFormControl.errors?.rangeFomInvalid)
    );
  }

  get godinaIsOutOfRange() {
    return (
      this.godinaFormControl.touched &&
      this.godinaFormControl.dirty &&
      this.godinaFormControl.invalid &&
      this.godinaFormControl.errors?.minMaxInvalid
    );
  }

  private getInputNamesBezSufiksa(): {
    godinaOd: string;
    godinaDo: string;
    datumOd: string;
    datumDo: string;
  } {
    const name = this.inputParam.attributeMetadata.name;
    const indexOfSuffix = name.lastIndexOf('$');
    const attrName =
      indexOfSuffix < 0 ? name : name.substring(0, indexOfSuffix);

    return {
      godinaOd: attrName + '_godina' + '$from',
      godinaDo: attrName + '_godina' + '$to',
      datumOd: attrName + '$from',
      datumDo: attrName + '$to'
    };
  }

  validiraj() {
    const { godinaOd, godinaDo, datumOd, datumDo } =
      this.getInputNamesBezSufiksa();

    const datumFromControl = this.formService.getFormControl(datumOd);
    const datummToControl = this.formService.getFormControl(datumDo);
    const godinaFromControl = this.formService.getFormControl(godinaOd);
    const godinaToControl = this.formService.getFormControl(godinaDo);

    datumFromControl.updateValueAndValidity();
    datummToControl.updateValueAndValidity();
    godinaFromControl.updateValueAndValidity();
    godinaToControl.updateValueAndValidity();
  }

  private handleFromWithoutTo(item: Item) {
    if (
      item['en~jedinica_gradiva~vrijeme_nastanka$from'] != null &&
      item['en~jedinica_gradiva~vrijeme_nastanka$to'] == null
    ) {
      this.selectedOption.set('Datum');
    }
  }

  private clearFormControls(selectedOption: VrijemeInputOption | undefined) {
    if (selectedOption !== 'Datum') this.datumFormControl.setValue(null);
    if (selectedOption !== 'Godina') this.godinaFormControl.setValue(null);
  }
}
